import MoreVertIcon from '@mui/icons-material/MoreVert'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import * as React from 'react'
import { useDispatch } from 'react-redux'
import { NavLink } from 'react-router'
import { message } from 'services/snackbar'
import styles from './AppsRoute.pcss'

const Clipboard = require('clipboard')

export type AppCurrency = 'usd' | 'inr'

export const DEFAULT_TIER = 1
export const DEFAULT_CURRENCY: AppCurrency = 'usd'
/* INR is billed on a single plan — the backend expects tier 3 with it. */
export const INR_TIER = 3

export interface AppInfo {
  id: string
  userId: string
  adminLimit: string
  active: string
  product: string
  email: string
  chargifySubscriptionId: string
  stripeSubscriptionId?: string
  fullName: string
  profilesLimit?: number
  postsPerDay?: number
  posts: any
  allowedLocations?: number
  isBeta: '0' | '1'
  masterAdmin: '0' | '1'
  tier?: number
  currency?: AppCurrency
  fbTokens: string[]
  userBlocked: any
  impersonateAccessToken: string
  config?: {
    igDirect?: boolean
  }
  nextRenewal?: string
}

interface AppTableRowProps {
  app: AppInfo
  onClick: (app: AppInfo) => void
  onClearFBTokens: (userId: string) => void
  toggleUserBlocked: (userId: string, current: boolean) => void
  resetPassword: (userId: string) => void
  clearUserData: (userId: string) => void
  restartTrial: (appId: string) => void
  onViewData: (userId: string) => void
  onImpersonate: (user: { userId: string, product: string, fullName: string, accessToken: string }) => void
  makeMasterAdmin: (userId: string, appId: string) => void
  toggleBlockApp: (appId: string) => void
}
export function AppTableRow({
  app,
  onClick,
  onClearFBTokens,
  toggleUserBlocked,
  resetPassword,
  clearUserData,
  onViewData,
  restartTrial,
  onImpersonate,
  makeMasterAdmin,
  toggleBlockApp
}: AppTableRowProps) {
  const dispatch = useDispatch()
  const {
    id, userId, product, email, fullName,
    posts, fbTokens, masterAdmin, userBlocked, stripeSubscriptionId, tier, currency
  } = app
  const [anchorEl, setAnchorEl] = React.useState(null)
  const clipboardRef = React.useRef(null)

  const isBlocked = parseInt(userBlocked, 10) > 0

  const setEmailRef = (element: HTMLElement | null) => {
    if (element) {
      if (clipboardRef.current) {
        (clipboardRef.current as any).destroy()
      }

      clipboardRef.current = new Clipboard(element, {
        text: () => email,
        container: element
      })
    }
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget as any)
  }

  const closeMenu = () => {
    setAnchorEl(null)
  }

  const menuAction = (fn: () => void) => () => {
    closeMenu()
    fn()
  }

  const blockApp = () => {
    toggleBlockApp(app.id)
  }

  const toggleBlocked = () => {
    toggleUserBlocked(userId, isBlocked)
  }

  const onMakeMasterAdmin = () => {
    makeMasterAdmin(userId, app.id)
  }

  const onResetPassword = () => {
    resetPassword(userId)
  }

  const onRestartTrial = () => {
    restartTrial(id)
  }

  const onRowClick = () => {
    onClick(app)
  }

  const viewData = (e: React.MouseEvent) => {
    e.stopPropagation()
    onViewData(userId)
  }

  const clearData = (e: React.MouseEvent) => {
    e.stopPropagation()
    clearUserData(userId)
  }

  const clearTokens = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClearFBTokens(userId)
  }

  const onImpersonateClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onImpersonate({ userId, product, fullName, accessToken: app.impersonateAccessToken })
  }

  const stopPropagation = (e: any) => {
    e.stopPropagation()
  }

  const onEmailClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(message('Email copied to clipboard'))
  }

  return (
    <tr className={styles.tr} onClick={onRowClick}>
      <td className={styles['cell-s']}>{id}</td>
      <td className={styles['cell-s']} onClick={stopPropagation}>
        <NavLink to={`/admin/support/users?userId=${userId}`} target="_blank">{userId}</NavLink>
      </td>
      <td className={styles['cell-s']} onClick={stopPropagation}>
        {stripeSubscriptionId ? (
          <a href={`https://dashboard.stripe.com/subscriptions/${stripeSubscriptionId}`} target="_blank" rel="noopener">
            {stripeSubscriptionId}
          </a>
        ) : 'N/A'}
      </td>
      <td className={styles['cell-s']}>{tier ?? DEFAULT_TIER}</td>
      <td className={styles['cell-s']}>{(currency ?? DEFAULT_CURRENCY).toUpperCase()}</td>
      <td ref={setEmailRef} style={{ cursor: 'pointer' }} onClick={onEmailClick}>{email}</td>
      <td>{fullName}</td>
      <td className={styles['cell-s']}>
        <Button className={styles.btn} onClick={viewData}>View</Button>
        <Button className={styles.btn} onClick={clearData}>Clear</Button>
      </td>
      <td className={styles['cell-s']}>{posts}</td>
      <td onClick={stopPropagation}>
        {fbTokens.map((token) => (
          <div>
            <a href={`https://developers.facebook.com/tools/debug/accesstoken?access_token=${fbTokens[0]}`} target="_blank">
              {`...${token.slice(-7)}`} {/* eslint-disable-line no-magic-numbers */}
            </a>
          </div>
        ))}
        {fbTokens.length === 0 && <div>N/A</div>}
        <Button className={styles.btn} onClick={clearTokens}>Clear</Button>
      </td>
      <td className={styles['cell-s']}>{masterAdmin === '1' ? 'Y' : 'N'}</td>
      <td>
        <Button
          variant="contained"
          color="primary"
          style={{ display: 'block' }}
          onClick={onImpersonateClick}
        >
          Impersonate
        </Button>
      </td>
      <td className={styles['cell-s']}>
        <IconButton
          aria-label="More"
          aria-haspopup="true"
          size="small"
          onClick={openMenu}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          classes={{ paper: styles.menu }}
          onClose={closeMenu}
          onClick={stopPropagation}
        >
          <MenuItem onClick={menuAction(onMakeMasterAdmin)}>
            Make master admin
          </MenuItem>
          <MenuItem onClick={menuAction(onResetPassword)}>
            Reset password
          </MenuItem>
          <MenuItem onClick={menuAction(onRestartTrial)}>
            Restart trial
          </MenuItem>
          <MenuItem onClick={menuAction(toggleBlocked)}>
            {isBlocked ? 'Unblock user' : 'Block user'}
          </MenuItem>
          <MenuItem onClick={menuAction(blockApp)}>
            Block/Unblock App
          </MenuItem>
        </Menu>
      </td>
      {/* <td>{props.app.active}</td>
      <td>{props.app.adminLimit}</td>
      <td>{props.app.product}</td>
      <td>{props.app.allowedLocations || 0}</td> */}
    </tr>
  )
}
